import React, { useState } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast } from "../../../utils/dialog";

export default function AdminCarouselManager() {
  const { carouselData, addCarouselCard, updateCarouselCard, deleteCarouselCard } = useData();

  const [newCard, setNewCard] = useState({
    title: "",
    description: "",
    image: "",
  });

  const [file, setFile] = useState(null); // File for upload
  const [editingCard, setEditingCard] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCard((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]); // Set the selected file
  };

  const handleAddCard = async () => {
    if (newCard.title && newCard.description) {
      await addCarouselCard(newCard, file); // Pass the file to the method
      setNewCard({ title: "", description: "", image: "" });
      setFile(null); // Reset the file input
    }
  };

  const handleUpdateCard = async () => {
    if (editingCard && newCard.title && newCard.description) {
      await updateCarouselCard(editingCard.id, newCard, file); // Pass the file to the method
      setEditingCard(null);
      setNewCard({ title: "", description: "", image: "" });
      setFile(null); // Reset the file input
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setNewCard({ title: card.title, description: card.description, image: card.image });
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete Carousel Card?",
        text: "This carousel card will be permanently deleted.",
      })
    ) {
      await deleteCarouselCard(id);
      successToast("Carousel card deleted successfully.");
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Manage Carousel</h2>

      {/* Add/Edit Form */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{editingCard ? "Edit Card" : "Add New Card"}</h3>
        <input
          type="text"
          name="title"
          placeholder="Title"
          value={newCard.title}
          onChange={handleInputChange}
          className="block w-full mb-2 p-2 border rounded"
        />
        <textarea
          name="description"
          placeholder="Description"
          value={newCard.description}
          onChange={handleInputChange}
          className="block w-full mb-2 p-2 border rounded"
        />
        <div className="mb-3">
          <ImageUploadField
            label="Image"
            hint="Recommended size: 600×400px"
            previewUrl={newCard.image}
            file={file}
            onChange={handleFileChange}
            onClear={() => setFile(null)}
          />
        </div>
        <button
          onClick={editingCard ? handleUpdateCard : handleAddCard}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          {editingCard ? "Update Card" : "Add Card"}
        </button>
        {editingCard && (
          <button
            onClick={() => {
              setEditingCard(null);
              setNewCard({ title: "", description: "", image: "" });
              setFile(null); // Reset the file input
            }}
            className="ml-2 px-4 py-2 bg-gray-500 text-white rounded"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Carousel List */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Carousel Cards</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {carouselData.map((card) => (
            <div key={card.id} className="p-4 border rounded shadow">
              <img src={card.image} alt={card.title} className="w-full h-32 object-cover mb-2" />
              <h4 className="text-lg font-bold">{card.title}</h4>
              <p className="text-sm text-gray-600">{card.description}</p>
              <div className="mt-2 flex justify-between">
                <button
                  onClick={() => handleEdit(card)}
                  className="px-3 py-1 bg-yellow-500 text-white rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(card.id)}
                  className="px-3 py-1 bg-red-500 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}